import { useState, useEffect, useRef } from 'react'
import { RefreshCw, ArrowLeft, Inbox as InboxIcon } from 'lucide-react'
import { Card } from '../ui'
import { timeAgo } from '../../utils/timeAgo'

const STATUS_CLS = {
  open: 'bg-amber-100 text-amber-700',
  pending: 'bg-blue-100 text-blue-700',
  resolved: 'bg-green-100 text-green-700',
}
const STATUS_LABEL = { open: 'Open', pending: 'Pending', resolved: 'Resolved' }

function StatusChip({ status }) {
  return <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${STATUS_CLS[status]}`}>{STATUS_LABEL[status]}</span>
}

function PriorityChip({ priority }) {
  if (priority !== 'high') return null
  return <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-red-100 text-red-700">High</span>
}

const FILTERS = [
  { key: 'active', label: 'Active', test: (t) => t.status !== 'resolved' },
  { key: 'resolved', label: 'Resolved', test: (t) => t.status === 'resolved' },
  { key: 'all', label: 'All', test: () => true },
]

export default function Inbox({ support, organizations }) {
  const { tickets, messages, fetchTickets, fetchMessages, sendMessage, setStatus, markRead } = support
  const [filter, setFilter] = useState('active')
  const [selectedId, setSelectedId] = useState(null)
  const [reply, setReply] = useState('')
  const [sending, setSending] = useState(false)
  const endRef = useRef(null)

  const orgName = (id) => organizations.find((o) => o.id === id)?.name || 'Unknown organization'
  const visible = tickets.filter(FILTERS.find((f) => f.key === filter).test)
  const selected = tickets.find((t) => t.id === selectedId) || null
  const thread = messages[selectedId] || []

  function select(t) {
    setSelectedId(t.id)
    fetchMessages(t.id)
    if (t.adminUnread) markRead(t.id)
  }

  useEffect(() => { endRef.current?.scrollIntoView({ block: 'end' }) }, [thread.length])

  async function handleSend() {
    const body = reply.trim()
    if (!body || !selected) return
    setSending(true)
    const result = await sendMessage(selected.id, body)
    setSending(false)
    if (result.success) setReply('')
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        {FILTERS.map((f) => (
          <button key={f.key} onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
              filter === f.key ? 'border-teal-600 bg-teal-50 text-teal-700' : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300'
            }`}>
            {f.label} · {tickets.filter(f.test).length}
          </button>
        ))}
        <span className="flex-1" />
        <button onClick={fetchTickets} title="Refresh"
          className="p-2 rounded-lg border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 transition">
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      </div>

      {tickets.length === 0 ? (
        <Card>
          <div className="px-6 py-12 text-center">
            <InboxIcon className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-sm font-semibold text-gray-600">No tickets yet</p>
            <p className="text-xs text-gray-400 mt-1">
              Customers open tickets from Profile → Contact support in the app. They'll land here.
            </p>
          </div>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-[320px,1fr] min-h-[480px]">

            {/* Ticket list — on mobile, hidden while a conversation is open */}
            <div className={`border-gray-100 md:border-r overflow-y-auto max-h-[70vh] ${selected ? 'hidden md:block' : ''}`}>
              {visible.length === 0 && (
                <p className="px-4 py-8 text-sm text-gray-400 text-center">Nothing in this view.</p>
              )}
              {visible.map((t) => (
                <button key={t.id} onClick={() => select(t)}
                  className={`w-full text-left px-4 py-3 border-b border-gray-100 transition ${
                    selectedId === t.id ? 'bg-teal-50 shadow-[inset_3px_0_0] shadow-teal-500' : 'hover:bg-gray-50'
                  }`}>
                  <div className="flex items-center gap-2">
                    {t.adminUnread && <span className="w-2 h-2 rounded-full bg-teal-500 flex-none" />}
                    <p className="flex-1 text-[13px] font-semibold text-gray-800 truncate">{t.subject}</p>
                    <span className="text-[11px] text-gray-400 flex-none">{timeAgo(t.lastMessageAt)}</span>
                  </div>
                  <p className="text-xs text-gray-400 truncate mt-0.5">{orgName(t.organizationId)}</p>
                  <div className="flex items-center gap-1.5 mt-1.5">
                    <StatusChip status={t.status} />
                    <PriorityChip priority={t.priority} />
                  </div>
                </button>
              ))}
            </div>

            {/* Conversation */}
            {!selected ? (
              <div className="hidden md:grid place-items-center text-sm text-gray-400 p-8">
                Select a ticket to read the conversation.
              </div>
            ) : (
              <div className="flex flex-col max-h-[70vh]">
                <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100">
                  <button onClick={() => setSelectedId(null)} className="md:hidden p-1.5 -ml-1 text-gray-400 hover:text-gray-600">
                    <ArrowLeft className="w-4 h-4" />
                  </button>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-gray-800 truncate">{selected.subject}</p>
                    <p className="text-xs text-gray-400 truncate">{orgName(selected.organizationId)}</p>
                  </div>
                  <StatusChip status={selected.status} />
                  <PriorityChip priority={selected.priority} />
                </div>

                <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
                  {thread.map((m) => (
                    <div key={m.id} className={`max-w-[80%] rounded-xl px-3.5 py-2.5 text-[13px] leading-relaxed ${
                      m.senderRole === 'support'
                        ? 'ml-auto bg-teal-50 text-gray-700 rounded-br-sm'
                        : 'bg-gray-100 text-gray-700 rounded-bl-sm'
                    }`}>
                      <p className="text-[11px] font-semibold opacity-60 mb-0.5">
                        {m.senderRole === 'support' ? 'You' : m.senderName || 'Customer'} · {timeAgo(m.createdAt)}
                      </p>
                      <p className="whitespace-pre-wrap">{m.body}</p>
                    </div>
                  ))}
                  <div ref={endRef} />
                </div>

                <div className="border-t border-gray-100 px-4 py-3 space-y-2">
                  <textarea value={reply} onChange={(e) => setReply(e.target.value)}
                    placeholder="Reply to the customer…" rows={2}
                    className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-teal-500 resize-y" />
                  <div className="flex justify-end gap-2">
                    {selected.status === 'resolved' ? (
                      <button onClick={() => setStatus(selected.id, 'open')}
                        className="text-xs font-semibold px-3.5 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition">
                        Reopen
                      </button>
                    ) : (
                      <button onClick={() => setStatus(selected.id, 'resolved')}
                        className="text-xs font-semibold px-3.5 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition">
                        Resolve
                      </button>
                    )}
                    <button onClick={handleSend} disabled={sending || !reply.trim()}
                      className="text-xs font-semibold px-3.5 py-2 rounded-lg bg-teal-600 hover:bg-teal-700 text-white disabled:opacity-50 transition">
                      {sending ? 'Sending…' : 'Send reply'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </Card>
      )}
    </div>
  )
}
